import { useState, type ChangeEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MIGRATION_ITEM_OUTCOMES,
  MigrationBatchItemSchema,
  MigrationBatchSchema,
  type MigrationBatchDto,
  type MigrationBatchItemDto,
  type MigrationReason,
} from '@taqon/contracts';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { ApiError, getData, getPage, postForm, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel, documentsKey } from '../../shared/documents/DocumentsPanel';
import { ExportButton } from '../../shared/export/ExportButton';
import { recordPath, viewCapability } from '../../shared/records/routes';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { ApprovalPanel, useSubmittedByMe } from '../../shared/workflow/ApprovalPanel';
import { Pager } from '../crm/common';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { BATCHES, ITEMS, when } from './common';

const PAGE_SIZE = 50;

type Step = 'validate' | 'submit' | 'withdraw' | 'apply' | 'reconcile' | 'lock';

/** Each step: when it is offered, and how it is confirmed (SDD §21.2). */
const STEPS: readonly {
  step: Step;
  label: string;
  statuses: readonly string[];
  title: string;
  message: string;
  tone: 'primary' | 'warning';
}[] = [
  {
    step: 'validate',
    label: 'Validate',
    statuses: ['staged', 'validated'],
    title: 'Validate the batch',
    message:
      'Every row is run through the owning module as a dry run and then rolled back: nothing is created. Each row records whether it would be accepted, and why not.',
    tone: 'primary',
  },
  {
    step: 'submit',
    label: 'Submit for approval',
    statuses: ['validated'],
    title: 'Submit for approval',
    message:
      'The batch goes to its approvers through the configured approval route. It cannot be validated again while awaiting a decision.',
    tone: 'primary',
  },
  {
    // Offered only to whoever submitted it (FRD §17 CLR-64); see `offered` below.
    step: 'withdraw',
    label: 'Withdraw from approval',
    statuses: ['submitted'],
    title: 'Withdraw from approval',
    message:
      'The batch leaves approval and goes back to validated. Its approvers are no longer asked to decide it; you can correct, validate and resubmit it.',
    tone: 'warning',
  },
  {
    step: 'apply',
    label: 'Apply',
    statuses: ['approved', 'applying'],
    title: 'Apply the batch',
    message:
      'Each pending row is created through the owning module. Accepted rows become records; rejected rows keep their reasons and leave nothing behind. An interrupted run can be resumed.',
    tone: 'warning',
  },
  {
    step: 'reconcile',
    label: 'Reconcile',
    statuses: ['applied'],
    title: 'Reconcile the batch',
    message:
      'The outcomes are compared to the control total. The batch is reconciled only when every row has an outcome, the counts agree and no rejection is unresolved. A reconciled batch keeps its reconciliation report as a document on the batch.',
    tone: 'primary',
  },
  {
    step: 'lock',
    label: 'Lock',
    statuses: ['reconciled'],
    title: 'Lock the batch',
    message:
      'A locked batch refuses every further change. Later corrections go through the normal process.',
    tone: 'warning',
  },
];

/**
 * One migration batch (`/migration/batches/:id`; SDD §21.2; FRD-MIG-021 to MIG-025, MIG-034): its
 * facts and counts, the reconciliation result, the upload of its source file, the pipeline steps
 * (each offered only in its status and confirmed first), its approval, its rows with outcomes and
 * reasons, the report download and its history.
 */
export function BatchDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const canCreate = hasCapability(session.data, 'migration.batch:create');
  const canChange = hasCapability(session.data, 'migration.batch:change');
  const [confirming, setConfirming] = useState<Step | null>(null);
  const detail = useQuery({
    queryKey: ['migration', 'batch', id],
    queryFn: () => getData(`${BATCHES}/${id}`, MigrationBatchSchema),
  });
  const submittedByMe = useSubmittedByMe(
    'migration.batch',
    id,
    detail.data?.status === 'submitted',
  );
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['migration'] });
  };
  const run = useMutation({
    mutationFn: ({ step, batch }: { step: Step; batch: MigrationBatchDto }) =>
      postJson(
        `${BATCHES}/${batch.id}/${step}`,
        {},
        MigrationBatchSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: batch.version },
      ),
    onSuccess: async (_batch, { step, batch }) => {
      setConfirming(null);
      await refresh();
      // Reconciling keeps the report as a document on the batch (SDD §21.2 step 6).
      if (step === 'reconcile') {
        await queryClient.invalidateQueries({
          queryKey: documentsKey('migration.batch', batch.id),
        });
      }
    },
  });

  const back = (
    <Button component={RouterLink} to="/migration/batches" startIcon={<ArrowBackIcon />}>
      All batches
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the batch" />;
  if (detail.isError) {
    return <RecordFailure title="Migration batch" noun="batch" error={detail.error} back={back} />;
  }
  const batch = detail.data;
  const offered = canChange
    ? STEPS.filter(
        (s) => s.statuses.includes(batch.status) && (s.step !== 'withdraw' || submittedByMe),
      )
    : [];
  const pending = STEPS.find((s) => s.step === confirming);

  return (
    <Stack spacing={3}>
      <PageHeader
        title={`${batch.batchNumber} — ${batch.targetLabel}`}
        description={batch.sourceReference}
        actions={back}
      />
      {(offered.length > 0 || batch.status !== 'created') && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
          {offered.map((s) => (
            <Button
              key={s.step}
              variant={s.step === 'lock' ? 'outlined' : 'contained'}
              color={s.tone}
              onClick={(event) => {
                event.currentTarget.focus();
                run.reset();
                setConfirming(s.step);
              }}
            >
              {batch.status === 'applying' && s.step === 'apply' ? 'Resume apply' : s.label}
            </Button>
          ))}
          {batch.status !== 'created' && (
            <ExportButton
              recordType="migration.batch"
              href={`${BATCHES}/${batch.id}/report.csv`}
              label="Download report"
            />
          )}
        </Stack>
      )}
      <Section title="Batch">
        <Facts
          label="Batch details"
          facts={[
            ['Status', <StatusChip key="s" status={batch.status} />],
            ['Batch number', batch.batchNumber],
            ['Target record type', `${batch.targetLabel} (${batch.targetRecordType})`],
            ['Source reference', batch.sourceReference],
            ['Source file', batch.sourceFileName ?? '—'],
            ['Control total', String(batch.controlRowCount)],
            ['Rows staged', String(batch.itemCount)],
            [
              'Dry run',
              batch.validatedAt === null
                ? 'Not validated yet'
                : `${String(batch.validCount)} valid, ${String(batch.invalidCount)} invalid (${when(batch.validatedAt)})`,
            ],
            [
              'Outcomes',
              `${String(batch.acceptedCount)} accepted, ${String(batch.rejectedCount)} rejected, ${String(batch.outOfScopeCount)} out of scope, ${String(batch.pendingCount)} pending`,
            ],
            ['Created', when(batch.createdAt)],
            ['Submitted', when(batch.submittedAt)],
            ['Approved', when(batch.approvedAt)],
            ['Applied', when(batch.appliedAt)],
            ['Locked', when(batch.lockedAt)],
            ...(batch.failureReason !== null
              ? ([['Failure', batch.failureReason]] as [string, string][])
              : []),
          ]}
        />
      </Section>
      {batch.reconciliation !== null && (
        <Section title="Reconciliation">
          <Box sx={{ p: 2, pb: 0 }}>
            <Alert
              severity={batch.reconciliation.reconciled ? 'success' : 'warning'}
              variant="outlined"
            >
              {batch.reconciliation.reconciled
                ? 'The batch agrees to its control total and every row is resolved. Its reconciliation report is kept under “Source and evidence documents”.'
                : 'The batch does not reconcile yet: see the difference, pending rows and unresolved rejections.'}
            </Alert>
          </Box>
          <Facts
            label="Reconciliation result"
            facts={[
              ['Control total', String(batch.reconciliation.controlRowCount)],
              ['Accepted', String(batch.reconciliation.accepted)],
              ['Rejected', String(batch.reconciliation.rejected)],
              ['Out of scope', String(batch.reconciliation.outOfScope)],
              ['Pending', String(batch.reconciliation.pending)],
              ['Difference', String(batch.reconciliation.difference)],
              ['Unresolved rejections', String(batch.reconciliation.unresolvedRejected)],
              ['Checked', when(batch.reconciliation.reconciledAt)],
            ]}
          />
        </Section>
      )}
      {batch.status === 'created' && canCreate && <UploadSection batch={batch} onDone={refresh} />}
      <ApprovalPanel
        recordType="migration.batch"
        recordId={batch.id}
        onDecided={() => {
          void refresh();
        }}
      />
      {batch.status !== 'created' && <ItemsSection batch={batch} />}
      <DocumentsPanel
        recordType="migration.batch"
        recordId={batch.id}
        canAttach={canChange && batch.status !== 'locked'}
        title="Source and evidence documents"
      />
      <RecordHistory recordType="migration.batch" recordId={batch.id} />
      {pending !== undefined && (
        <ConfirmDialog
          open
          title={pending.title}
          message={pending.message}
          confirmLabel={pending.label}
          tone={pending.tone}
          busy={run.isPending}
          error={run.isError ? run.error.message : null}
          onCancel={() => {
            setConfirming(null);
          }}
          onConfirm={() => {
            run.mutate({ step: pending.step, batch });
          }}
        />
      )}
    </Stack>
  );
}

/** Stages the source file: a UTF-8 CSV with the importer's header row (SDD §21.2 step 1). */
function UploadSection({
  batch,
  onDone,
}: {
  batch: MigrationBatchDto;
  onDone: () => Promise<void>;
}) {
  const session = useSession();
  const [file, setFile] = useState<File | null>(null);
  const upload = useMutation({
    mutationFn: (chosen: File) => {
      const form = new FormData();
      form.append('file', chosen);
      return postForm(
        `${BATCHES}/${batch.id}/file`,
        form,
        MigrationBatchSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: batch.version },
      );
    },
    onSuccess: onDone,
  });
  return (
    <Section title="Source file">
      <Stack spacing={2} sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Upload the approved source as CSV (UTF-8, header row, at most 5 MB). A file whose header
          does not match the importer is refused whole; rows with wrong values are staged and
          reported by validation.
        </Typography>
        {upload.isError && (
          <Alert severity="error" variant="outlined">
            {upload.error.message}
            {upload.error instanceof ApiError && upload.error.fieldErrors.length > 0 && (
              <ul>
                {upload.error.fieldErrors.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
          <Box
            component="input"
            id="migration-file"
            type="file"
            aria-label="Source CSV file"
            accept=".csv,text/csv"
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setFile(event.target.files?.[0] ?? null);
            }}
            sx={{ maxWidth: '100%' }}
          />
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            disabled={file === null || upload.isPending}
            onClick={() => {
              if (file !== null) upload.mutate(file);
            }}
          >
            {upload.isPending ? 'Uploading…' : 'Upload and stage'}
          </Button>
        </Stack>
      </Stack>
    </Section>
  );
}

const OUTCOME_LABELS: Readonly<Record<string, string>> = {
  pending: 'Pending',
  accepted: 'Accepted',
  rejected: 'Rejected',
  out_of_scope: 'Out of scope',
};

/** The batch's rows, filtered and paged on the server, with their outcomes and reasons. */
function ItemsSection({ batch }: { batch: MigrationBatchDto }) {
  const session = useSession();
  const canApprove = hasCapability(session.data, 'migration.batch:approve');
  const canViewTarget = hasCapability(session.data, viewCapability(batch.targetRecordType));
  const [params, setParams] = useSearchParams();
  const [marking, setMarking] = useState<MigrationBatchItemDto | null>(null);
  const outcome = params.get('outcome') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const set = (key: 'outcome' | 'page', value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };
  const query = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
  if (outcome !== '') query.set('filter[outcome]', outcome);
  const items = useQuery({
    queryKey: ['migration', 'items', batch.id, query.toString()],
    queryFn: () =>
      getPage(`${BATCHES}/${batch.id}/items?${query.toString()}`, MigrationBatchItemSchema),
  });

  return (
    <Section
      title="Rows"
      actions={
        <TextField
          select
          size="small"
          label="Outcome"
          value={outcome}
          onChange={(e) => {
            set('outcome', e.target.value);
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {MIGRATION_ITEM_OUTCOMES.map((o) => (
            <MenuItem key={o} value={o}>
              {OUTCOME_LABELS[o] ?? o}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      {items.isPending && <LoadingState label="Loading rows" />}
      {items.isError && <ErrorState message={items.error.message} />}
      {items.data?.items.length === 0 && <EmptyState title="No rows match" />}
      {items.data !== undefined && items.data.items.length > 0 && (
        <>
          <Table size="small" aria-label="Batch rows">
            <TableHead>
              <TableRow>
                <TableCell>Row</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Dry run</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell>Reasons</TableCell>
                <TableCell>Record</TableCell>
                <TableCell>Values</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.data.items.map((item) => {
                const path =
                  item.resultRecordId === null
                    ? null
                    : recordPath(item.targetRecordType, item.resultRecordId);
                return (
                  <TableRow key={item.id}>
                    <TableCell>{item.sourceRowNumber}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.sourceRowKey ?? '—'}</TableCell>
                    <TableCell>
                      {item.validationResult === null ? (
                        '—'
                      ) : (
                        <Stack spacing={0.5}>
                          <StatusChip status={item.validationResult} />
                          <Reasons reasons={item.validationReasons} />
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={item.outcome} />
                    </TableCell>
                    <TableCell>
                      <Reasons reasons={item.rejectionReasons} />
                      {item.outOfScopeReason !== null && (
                        <Typography variant="caption" component="p">
                          Out of scope: {item.outOfScopeReason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {item.resultRecordId === null ? (
                        '—'
                      ) : path !== null && canViewTarget ? (
                        <RecordLink to={path}>Open record</RecordLink>
                      ) : (
                        `#${item.resultRecordId}`
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
                        {Object.entries(item.payload)
                          .filter(([, v]) => v !== null)
                          .map(([k, v]) => `${k}: ${v ?? ''}`)
                          .join(' · ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {canApprove && batch.status === 'applied' && item.outcome === 'rejected' && (
                        <Button
                          size="small"
                          onClick={(event) => {
                            event.currentTarget.focus();
                            setMarking(item);
                          }}
                        >
                          Accept as out of scope
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Pager
            page={page}
            pageSize={PAGE_SIZE}
            total={items.data.total}
            noun="row(s)"
            onPage={(next) => {
              set('page', String(next));
            }}
          />
        </>
      )}
      {marking !== null && (
        <OutOfScopeDialog
          item={marking}
          onClose={() => {
            setMarking(null);
          }}
        />
      )}
    </Section>
  );
}

function Reasons({ reasons }: { reasons: readonly MigrationReason[] }) {
  if (reasons.length === 0) return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2 }}>
      {reasons.map((r, i) => (
        <Typography component="li" variant="caption" key={`${r.field}-${r.rule}-${String(i)}`}>
          <strong>{r.field}</strong>: {r.message}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * Accepts a rejected row as out of scope, with the reason (FRD-MIG-025: "explicitly accepted as
 * out of scope by an authorised approver"). Consequential and audited, so it is a form of its own.
 */
function OutOfScopeDialog({ item, onClose }: { item: MigrationBatchItemDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () =>
      postJson(
        `${ITEMS}/${item.id}/out-of-scope`,
        { reason: reason.trim() },
        MigrationBatchItemSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: item.version },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['migration'] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={`Accept row ${String(item.sourceRowNumber)} as out of scope`}
      submitLabel="Accept as out of scope"
      canSubmit={reason.trim() !== ''}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <Typography variant="body2">
        The row keeps its rejection reasons and is not loaded. The decision and its reason are
        recorded in the audit trail.
      </Typography>
      <TextField
        label="Reason"
        required
        multiline
        minRows={2}
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 500 } }}
      />
    </FormDialog>
  );
}
